<script lang="ts" setup>
import ElFormOrganization from '@/components/settings/components/ElForm/ElFormOrganization.vue'
import {
    OrganizationMs,
    useActiveOrganization,
    useFetchApiRequestAsync,
    useProfileUpdate
} from 'auth-package'
import { ref } from 'vue'
import { Delete } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import i18n from '@/i18n'

const { t } = i18n.global
const activeOrganization = useActiveOrganization()
const organizationForm = ref<InstanceType<typeof ElFormOrganization>>()

/**
 * Submits the organization form.
 * If the form is valid, then updates the organization.
 * If an error occurs, it shows an error message.
 */
function onSubmit() {
    organizationForm.value?.onSubmit()
}

/**
 * Deletes an organization with the given UUID.
 * After the organization is deleted, it updates the profile.
 * If an error occurs, it shows an error message.
 *
 * @param organizationUUId - The UUID of the organization to delete.
 */
async function deleteOrganization(organizationUUId: string) {
    try {
        const deletedOrganization = OrganizationMs.DeleteOrganization().setParameters({
            organizationId: organizationUUId
        })

        const response = await useFetchApiRequestAsync(deletedOrganization)

        if (response) {
            setTimeout(async () => {
                await useProfileUpdate()
            }, 1500)
        }
    } catch (error) {
        console.error('Error: When try to delete organization', error)
    }
}

/**
 * Shows a confirmation box to delete an organization.
 * If confirmed, then it deletes the organization and shows a success message.
 * If canceled, then it does nothing.
 *
 * @param organizationUUId - The UUID of the organization to delete.
 */
function deleteOrganizationConfirmBox(organizationUUId: string) {
    ElMessageBox.confirm(t('settings.deleteOrganization'), t('settings.warning'), {
        confirmButtonText: t('settings.delete'),
        cancelButtonText: t('settings.cancel'),
        type: 'warning'
    }).then(() => {
        deleteOrganization(organizationUUId)
        ElMessage({
            type: 'success',
            message: t('settings.deleteComplete')
        })
    })
}
</script>

<template>
    <el-card shadow="never">
        <ElFormOrganization
            ref="organizationForm"
            :organizationUUId="activeOrganization.uuid"
            isEdit
        />
        <div class="d-flex w-100">
            <el-tooltip :content="$t('settings.deleteOrganizationTooltip')">
                <el-button
                    class="ms-auto mt-2"
                    @click="deleteOrganizationConfirmBox(activeOrganization.uuid)"
                    type="danger"
                    :icon="Delete"
                    circle
                    text
                />
            </el-tooltip>
            <el-button type="primary" @click="onSubmit" class="mt-2">
                {{ $t('settings.save') }}
            </el-button>
        </div>
    </el-card>
</template>

<style lamg="scss" scoped></style>
